import {
  buildPayload,
  catalogBySlug,
  fitModelWithOptions,
  LAYOFF_WEEKS,
  layoffLabel,
  MODEL_LEVELS,
  modelHasFocus,
  modelHasOptionalRows,
  modelSpecialMuscles,
  muscleKey,
  pickLocalizedText,
  PROGRAM_FOCUS,
  SESSION_MINUTES,
  type BuildReasonCode,
  type CatalogMuscleSlug,
  type EffortScale,
  type Exercise,
  type LayoffWeeks,
  type Locale,
  type ModelLevel,
  type ModelOptionChoices,
  type NewProgram,
  type Program,
  type ProgramFocus,
  type ProgramModel,
  type SessionMinutes,
} from "@heraion/shared";

import { focusLabel } from "../ProgramComposer/ModelFocusRow";
import { difficultyLabel } from "./difficultyLabel";
import type { LibraryDifficulty } from "./libraryFilters";

// #472: a Metria model shown as a ready program. Its axes, the choices a
// follow starts on, the program those choices write, and what its card says.
// View, Follow and Customise all go through followDefaults and
// buildModelFollow, so the preview is the program that gets written.

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** One value per axis: what a follow writes. */
export type FollowChoices = {
  level: ModelLevel;
  sessionsPerWeek: number;
  minutes: SessionMinutes;
  focus?: ProgramFocus;
  blocks: number;
  /** #479: absent, no option. */
  options?: ModelOptionChoices;
};

/** The values each axis of a model offers. */
export type ModelAxes = {
  levels: readonly ModelLevel[];
  sessionsPerWeek: readonly number[];
  minutes: readonly SessionMinutes[];
  focus: readonly ProgramFocus[];
  /** #479: the options the model admits, each besides «none». Empty: not
   *  offered. */
  options: { layoffWeeks: readonly LayoffWeeks[]; muscles: readonly CatalogMuscleSlug[] };
};

type AxesSource = Pick<ProgramModel, "levels" | "sessionsPerWeek" | "days" | "daysBySessions" | "focus" | "options">;

/** The longest session: every exercise, and the only time when no row
 *  can drop out. */
export const FULL_MINUTES: SessionMinutes = SESSION_MINUTES[SESSION_MINUTES.length - 1]!;

/** The session length a follow starts on when rows can drop out. */
const DEFAULT_OPTIONAL_MINUTES: SessionMinutes = 60;

/** `exercises`: the catalog, for the muscles a specialization offers
 *  (modelSpecialMuscles). Absent: none. */
export const modelAxes = (model: AxesSource, exercises?: ReadonlyMap<string, Exercise>): ModelAxes => ({
  levels: MODEL_LEVELS.filter((l) => model.levels.includes(l)),
  sessionsPerWeek: [...model.sessionsPerWeek],
  minutes: modelHasOptionalRows(model) ? SESSION_MINUTES : [FULL_MINUTES],
  focus: modelHasFocus(model) ? PROGRAM_FOCUS : model.focus ? [model.focus] : [],
  options: {
    layoffWeeks: model.options?.comeback ? LAYOFF_WEEKS : [],
    muscles: modelSpecialMuscles(model, exercises),
  },
});

/** Some axis has a choice: Customise is offered. An option is one with a
 *  single value, since «none» is the other. */
export const modelHasChoices = (axes: ModelAxes): boolean =>
  [axes.levels, axes.sessionsPerWeek, axes.minutes, axes.focus].some((values: readonly unknown[]) => values.length > 1) ||
  axes.options.layoffWeeks.length > 0 ||
  axes.options.muscles.length > 0;

/** #479: a rule, not a ready program: it varies on level, days or focus. */
export const modelIsRule = (axes: ModelAxes): boolean =>
  axes.levels.length > 1 || axes.sessionsPerWeek.length > 1 || axes.focus.length > 1;

/** Program.name's schema max: the wizard's field and its derived name. */
export const PROGRAM_NAME_MAX = 120;
const SEP = " · ";
const cut = (s: string, n: number): string =>
  s.length <= n ? s : `${s.slice(0, Math.max(0, n - 1)).trimEnd()}…`;

/** #479: the model's name, then each varying axis and each option on
 *  («Focus · 45 min»); the cap cuts the name, never the axes. */
export const variantName = (
  base: string,
  axes: ModelAxes,
  c: Pick<FollowChoices, "level" | "sessionsPerWeek" | "minutes" | "focus" | "options">,
  t: Translate,
): string => {
  // Only an option the model admits: the one the build writes.
  const comeback = axes.options.layoffWeeks.length > 0 ? c.options?.comeback : undefined;
  const specialization = axes.options.muscles.length > 0 ? c.options?.specialization : undefined;
  const head = [
    ...(axes.levels.length > 1 ? [difficultyLabel(c.level, t)] : []),
    ...(axes.focus.length > 1 && c.focus ? [focusLabel(c.focus, t)] : []),
    ...(axes.sessionsPerWeek.length > 1 ? [t("library.catalog.perWeek", { count: c.sessionsPerWeek })] : []),
    ...(comeback
      ? [t("editorV3.model.nameComeback", { n: layoffLabel(comeback.layoffWeeks), defaultValue: "Comeback {{n}} wk" })]
      : []),
    ...(specialization
      ? [t("editorV3.model.nameSpecial", { muscle: t(muscleKey(specialization.muscle)), defaultValue: "+{{muscle}}" })]
      : []),
  ];
  const mins =
    axes.minutes.length > 1 ? [t("editorV3.model.nameMinutes", { n: c.minutes, defaultValue: "{{n}} min" })] : [];
  const tail = [...head, ...mins];
  const room = PROGRAM_NAME_MAX - tail.join(SEP).length - (tail.length > 0 ? SEP.length : 0);
  if (room >= 2) return [cut(base, room), ...tail].join(SEP);
  // No room left for the name: the longest axis but the minutes is cut.
  const over = tail.join(SEP).length - PROGRAM_NAME_MAX;
  if (over > 0 && head.length > 0) {
    const i = head.reduce((best, p, j) => (p.length > head[best]!.length ? j : best), 0);
    head[i] = cut(head[i]!, head[i]!.length - over);
  }
  return [...head, ...mins].join(SEP);
};

/** The library's filters a follow starts from. */
export type FollowFilters = {
  levels?: ReadonlySet<LibraryDifficulty>;
  perWeek?: ReadonlySet<number>;
};

/** The choices a follow starts on: the filtered level, then the profile's
 *  (none yet, #183), then the lowest; the filtered days, else the first;
 *  60 minutes when rows can drop out (the logger's More time adds them). */
export const followDefaults = (
  model: AxesSource & Pick<ProgramModel, "blocks">,
  filters: FollowFilters = {},
  profileLevel?: ModelLevel,
): FollowChoices => {
  const axes = modelAxes(model);
  const level =
    axes.levels.find((l) => filters.levels?.has(l)) ??
    axes.levels.find((l) => l === profileLevel) ??
    axes.levels[0]!;
  const focus = modelHasFocus(model) ? (model.focus ?? PROGRAM_FOCUS[0]) : model.focus;
  return {
    level,
    sessionsPerWeek:
      axes.sessionsPerWeek.find((n) => filters.perWeek?.has(n)) ?? axes.sessionsPerWeek[0]!,
    minutes: modelHasOptionalRows(model) ? DEFAULT_OPTIONAL_MINUTES : FULL_MINUTES,
    ...(focus ? { focus } : {}),
    blocks: model.blocks[0]!,
  };
};

export type FollowBuild =
  | { ok: true; program: NewProgram }
  | { ok: false; code: BuildReasonCode; reason: string };

/** The program a follow of `model` writes at `choices`, or why the build
 *  refuses it (#400). With an id, the program keeps its origin. */
export const buildModelFollow = (
  model: Omit<ProgramModel, "id"> & { id?: string },
  choices: FollowChoices & { name?: string; exercisePicks?: Record<string, string>; pinned?: ReadonlySet<string> },
  deps: {
    catalog: ReadonlyMap<string, Exercise> | ReadonlyArray<Exercise> | null | undefined;
    effortScale: EffortScale;
    lang: Locale;
  },
): FollowBuild => {
  const { catalog, effortScale, lang } = deps;
  const bySlug: ReadonlyMap<string, Exercise> =
    catalog instanceof Map ? catalog : catalogBySlug((catalog as ReadonlyArray<Exercise> | null) ?? []);
  const { name, exercisePicks, focus, options, ...rest } = choices;
  const { draft } = fitModelWithOptions(model, {
    ...rest,
    name: name ?? pickLocalizedText(model.name, lang),
    ...(focus ? { focus } : {}),
    ...(exercisePicks ? { exercisePicks } : {}),
    effortScale,
    exercises: bySlug,
    editLocale: lang,
    exerciseName: (slug) => {
      const ex = bySlug.get(slug);
      return ex ? pickLocalizedText(ex.name, lang) : undefined;
    },
    ...(model.id ? { modelId: model.id } : {}),
  }, options);
  // A model has no kg (§15 row 29): the unit is never read.
  const built = buildPayload(draft, "kg");
  return built.ok ? { ok: true, program: built.payload } : built;
};

const range = (values: readonly number[]): { from: number; to: number } => ({
  from: Math.min(...values),
  to: Math.max(...values),
});

/** The card's chip (the levels) and its meta line (goal, days, minutes). */
export const modelCardText = (
  model: ProgramModel,
  t: Translate,
): { chip: string; meta: string } => {
  const first = model.levels[0]!;
  const last = model.levels[model.levels.length - 1]!;
  const chip =
    model.levels.length === 1
      ? difficultyLabel(first, t)
      : t("library.catalog.levelRange", {
          from: difficultyLabel(first, t),
          to: difficultyLabel(last, t),
        });
  const { focus } = followDefaults(model);
  const days = range(model.sessionsPerWeek);
  const parts = [
    ...(focus ? [focusLabel(focus, t)] : []),
    days.from === days.to
      ? t("library.catalog.perWeek", { count: days.from })
      : t("library.catalog.perWeekRange", days),
    ...(modelHasOptionalRows(model)
      ? [t("library.catalog.minutesRange", range(SESSION_MINUTES))]
      : []),
  ];
  return { chip, meta: parts.join(" · ") };
};

/** The card's Follow/Clone row: its words carry the state, never colour
 *  alone. On a Metria program it is the Follow gesture (PL-1/PL-2). The hint
 *  says where the program lands, or (#477) where «Go to your copy» goes; a
 *  disabled «Already yours» gets none, it would ask for what is done. */
export const cloneRowText = (
  s: {
    waiting: boolean;
    cloning: boolean;
    alreadyCloned: boolean;
    /** #475: the copy is queued, the list does not show it yet. */
    copyQueued?: boolean;
    goToCopy: boolean;
    copies: number;
    followGesture: boolean;
    readOnly: boolean;
  },
  t: Translate,
): { label: string; hint?: string } => {
  const followHint = t("program.followHint", {
    defaultValue: "Follow this program (it shows up in In progress)",
  });
  if (s.waiting)
    return {
      label: t("library.modelWaitCatalog", { defaultValue: "Loading the exercises…" }),
      ...(s.followGesture && !s.cloning ? { hint: followHint } : {}),
    };
  if (s.cloning)
    return {
      label: s.followGesture
        ? t("library.following", { defaultValue: "Following…" })
        : t("library.catalog.cloning", { defaultValue: "Cloning…" }),
    };
  if (s.alreadyCloned) {
    if (s.copyQueued)
      return { label: t("library.copyQueued", { defaultValue: "Queued, shows up when sent" }) };
    if (!s.goToCopy) return { label: t("library.alreadyYours", { defaultValue: "Already yours" }) };
    return {
      label:
        s.copies > 1
          ? t("library.goToCopies", { defaultValue: "Go to your copies" })
          : t("library.goToCopy", { defaultValue: "Go to your copy" }),
      hint: t("library.goToCopyHint", { defaultValue: "Opens $t(library.tabPersonal), where your copy is." }),
    };
  }
  if (s.followGesture)
    return {
      label: t("program.follow", { defaultValue: "Follow" }),
      hint: followHint,
    };
  return {
    label: s.readOnly
      ? t("library.cloneFollowed", { defaultValue: "Make it yours" })
      : t("library.clone", { defaultValue: "Clone" }),
  };
};

/** Read-only stand-in for a built follow, as a template's is. Never sent. */
export const followPreviewProgram = (p: NewProgram, id: string): Program => ({
  ...p,
  id,
  createdBy: "metria",
  authoredAs: "athlete",
  assignedTo: ["metria"],
  status: "draft",
  createdAt: "1970-01-01T00:00:00.000Z",
  updatedAt: "1970-01-01T00:00:00.000Z",
  sessions: p.sessions.map((s, i) => ({
    ...s,
    id: s.id ?? `s${i}`,
    slots: s.slots.map((slot, j) => ({ ...slot, id: slot.id ?? `s${i}-${j}` })),
  })),
});

/** The card's View: the program Follow writes at `choices`. */
export const modelPreviewProgram = (
  model: ProgramModel,
  choices: FollowChoices,
  deps: Parameters<typeof buildModelFollow>[2],
): Program | null => {
  const built = buildModelFollow(model, choices, deps);
  return built.ok ? followPreviewProgram(built.program, model.id) : null;
};
